import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = anon, object = signed in
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch (e) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("ybm_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("ybm_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    localStorage.setItem("ybm_token", data.token);
    setUser(data.user);
    return data.user;
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Exchange Emergent Google Auth session_id for our JWT, then mark user signed-in.
  const completeGoogleSignIn = async (sessionId) => {
    const { data } = await api.post("/auth/google-session", { session_id: sessionId });
    localStorage.setItem("ybm_token", data.token);
    setUser(data.user);
    return data.user;
  };

  // Read a homepage "pending prompt" stashed by HomepageCreator. Returns null + clears after read.
  const consumePendingPrompt = () => {
    try {
      const raw = sessionStorage.getItem("ybm_pending_prompt");
      if (!raw) return null;
      sessionStorage.removeItem("ybm_pending_prompt");
      return JSON.parse(raw);
    } catch { return null; }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    localStorage.removeItem("ybm_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, completeGoogleSignIn, logout, refresh, setUser, consumePendingPrompt }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

// Helpers
export function effectiveTier(user) {
  if (!user) return null;
  if (user.role === "owner" && user.impersonated_tier) {
    return user.impersonated_tier === "free" ? null : user.impersonated_tier;
  }
  if (!user.subscription_tier || user.subscription_tier === "none") return null;
  if (user.subscription_expires_at) {
    const exp = new Date(user.subscription_expires_at);
    if (exp < new Date()) return null;
  }
  return user.subscription_tier;
}

export function planLabel(user) {
  const t = effectiveTier(user);
  if (!t) return "Free Trial";
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/**
 * isPremiumOrOwner — true whenever the user should be treated as Premium for
 * gate-keeping purposes. Owner is ALWAYS treated as Premium regardless of
 * which tier they're currently impersonating — the impersonator is for
 * inspecting what customers see, not for taking the owner's powers away.
 *
 * Use this for: BYOK access, voiceover access, no-watermark, white-label,
 * and any other Premium-only surface. Mirrors backend `_allow()` in keys_routes.
 */
export function isPremiumOrOwner(user) {
  if (!user) return false;
  if (user.role === "owner") return true;
  return effectiveTier(user) === "premium";
}